import {
  NOT,
  YES
} from '../../../lib/Boolean.lib';
import Core from '../../../lib/Core';
import {
  Fun
} from '../../../lib/Function.lib';
import useState from '../../../lib/hooks/useState.hook';
import {
  removeHtmlBlankLines
} from '../../../lib/HTML.lib';
import {
  Str
} from '../../../lib/String.lib';
import Box from '../../Layout/Wrappers/Box';
import Button from '../../Layout/Wrappers/Button';
import Card from '../../Layout/Wrappers/Card';
import Divider from '../../Layout/Wrappers/Divider';
import Fieldset from '../../Layout/Wrappers/Fieldset';
import {
  Html
} from '../../Layout/Wrappers/Html';
import Icon from '../../Layout/Wrappers/Icon';
import IconButton from '../../Layout/Wrappers/IconButton';
import RichTextBox from '../../Layout/Wrappers/RichTextBox';
import {
  PLACEMENT__RIGHT_START
} from '../../Layout/Wrappers/StyledTooltip';
import Typography from '../../Layout/Wrappers/Typography';
import {
  CANDIDATE_UI
} from '../CandidateUI.dic';

/**
 * @function CandidateSubmissionNotes
 * @description Render a Fieldset with a RichTextBox to edit the submissionNotes of a candidate.
 *              The RichTextBox is only enabled if the user is the recruiter.
 *              The Fieldset content is a Card if YES__TYPE_CARD is true, otherwise it is a Fieldset.
 *              The Card has a save button to save the submissionNotes.
 *              The Fieldset has a View 10x10 auto generated summary button to open a sticky note.
 *              If the user is not the recruiter, the Fieldset content is a Box with a placeholder.
 * @param {object} props - The props object.
 * @param {bject} [props.candidate] - The candidate object.
 * @param {function} [props.onChange] - The onChange function.
 * @param {boolean} [props.card] - If true, the content is a Card.
 * @param {function} [props.openStickyNote] - The function to open the sticky note.
 * @returns {object} The Fieldset or Card element.
 */
export default function CandidateSubmissionNotes({
  candidate: propsCandidate = {},
  onChange = Fun,
  openStickyNote = Fun,
  card: YES__TYPE_CARD = false
}) {
  const [candidate, updateCandidate] = useState(propsCandidate);

  const VALUE__SUBMISSION_NOTES = Str(
    YES__TYPE_CARD
      ? candidate.submissionNotes
      : propsCandidate.submissionNotes
  );

  const YES__USER_IS_THE_RECRUITER = (
    Core.getUserId() === propsCandidate.accountId
  );

  const YES__VALUE__SUBMISSION_NOTES = YES(
    removeHtmlBlankLines(
      VALUE__SUBMISSION_NOTES
    )
  );

  const NOT__VALUE__SUBMISSION_NOTES = NOT(YES__VALUE__SUBMISSION_NOTES);

  const ON_CHANGE__SUBMISSION_NOTES = (
    YES__TYPE_CARD
      ? (submissionNotes) => updateCandidate({ submissionNotes })
      : onChange
  )

  const CONTENT = (
    <Box row flex1
      onFocus={openStickyNote}
      className={YES__TYPE_CARD ? 'm-05' : ''}
    >
      {YES__USER_IS_THE_RECRUITER ? (
        <RichTextBox
          name='submissionNotes'
          placeholder={CANDIDATE_UI.submissionNotes.placeholder}
          value={VALUE__SUBMISSION_NOTES}
          onChange={ON_CHANGE__SUBMISSION_NOTES}
          minH10LH={NOT__VALUE__SUBMISSION_NOTES}
        />
      ) : (
        <Box row flex1
          className='p-1 outlined rounded scroll-y'
          title={CANDIDATE_UI.submissionNotes.tooltipReadOnly}
          placement={PLACEMENT__RIGHT_START}
        >
          {YES__VALUE__SUBMISSION_NOTES ? (
            <Html
              value={VALUE__SUBMISSION_NOTES}
              className='opacity-50'
            />
          ) : (
            <Box className='placeholder'>
              {CANDIDATE_UI.submissionNotes.empty}
            </Box>
          )
          }
        </Box>
      )}
    </Box>
  )
  return (
    YES__TYPE_CARD ? (
      <Card
        role='RecruiterWriteUpCard'
        className='mb-05'
      >
        <Typography strong large alignCenter blackDark
          className='m-1'
          style={{ height: 36 }}
        >
          RECRUITER WRITE UP
          <IconButton
            acl={YES__USER_IS_THE_RECRUITER}
            onClick={(event) => onChange(VALUE__SUBMISSION_NOTES)}
            icon='save'
          />
        </Typography>
        <Divider />
        {CONTENT}
      </Card>
    ) : (
      <Fieldset
        title={CANDIDATE_UI.submissionNotes.title}
        dot={'orange'}
        nextToTitle={
          <Icon
            icon='help_outline'
            title={CANDIDATE_UI.submissionNotes.tooltip}
            placement='top'
            paperStyle={{ maxWidth: 540 }}
            className='c-purple help icon16'
          />
        }
        actionBar={
          <>
            <Button
              onClick={openStickyNote}
              variant='text'
              className='c-black-medium tt-unset ms_button'
              startIcon={<i className='material-icons c-inherit'>sticky_note_2</i>}
            >
              View 10x10 auto generated summary
            </Button>
          </>
        }
      >
        {CONTENT}
      </Fieldset>
    )
  );
}
